import { Injectable, Injector } from '@angular/core';
import { ApplicationService } from '@app/shared/application/application.service';
import { RequirementConfigurationService } from '@app/shared/services/requirement-configuration.service';
import { AppComponentBase } from '@shared/common/app-component-base';
import { ApplicationStatus, ApplicationType, ApplicationUploadFilesInput, CreateLegDto, ExternalSystemEnum, GeneralDocumentGroupDto, GeneralFileDto, GeneralFileInfoDto, GetLegDocumentsInput, GetLegFileInput, GetLegIdentificationOutput, GetLegOutput, LegAuthorizationDto, LegAuthorizationServiceProxy, LegDocumentServiceProxy, LegDocumentsOutput, LegDto, LegIdentificationDto, LegIdentificationServiceProxy, LegPersonDto, LegRegisterOutput, LegServiceProxy, LegStatus, LegUidDataDto, LegUidSearchServiceProxy, PersonApplicationSummaryDto, SponsorshipApplicationSummaryDto, UserIdentificationDto } from '../../../shared/service-proxies/service-proxies';
import { delay, map, Observable, of, ReplaySubject, tap } from 'rxjs';

@Injectable({providedIn: 'root'})
export class LegService extends AppComponentBase {

    leg: LegDto;
    // liaResultDocuments: GeneralFileInfoDto[];
    private legsForSponsorshipSubject = new ReplaySubject<SponsorshipApplicationSummaryDto[]>(1);
    readonly legsForSponsorship$ = this.legsForSponsorshipSubject.asObservable();

    constructor(
        injector: Injector,
        private applicationService: ApplicationService,
        private requirementConfigurationService: RequirementConfigurationService,
        private legService: LegServiceProxy,
        private legDocumentService: LegDocumentServiceProxy,
        private uidSearchService: LegUidSearchServiceProxy,
        private authorityService: LegAuthorizationServiceProxy,
        private identificationService: LegIdentificationServiceProxy,
    ) {
        super(injector);
        console.log('LegService constructor');
    }

    createLeg(applicationType: ApplicationType): Observable<string> {
        this.closeLeg();
        return this.legService.createLeg(new CreateLegDto({ type: applicationType, uid: null})).pipe();
    }

    getLeg(caseId: string): Observable<GetLegOutput> {
        this.closeLeg();
        return this.legService.getLeg(caseId).pipe(
            tap((output: GetLegOutput) => {
                this.leg = output.leg;
            })
        );
    }

    closeLeg(): void {
        this.leg = null;
    }

    loadLegsForSponsorship(uid: string): Observable<SponsorshipApplicationSummaryDto[]> {
        if (this.isEmpty(uid)) {
            return of([]);
        }

        return this.legService.getLegsForSponsorship(uid).pipe(
            tap(legs => this.legsForSponsorshipSubject.next(legs))
        );
    }

    validateGlnForLegitimaiton(gln: string, professionId: string): Observable<LegRegisterOutput> {
        return this.legService.validateGlnForLegitimation(gln, professionId, this.caseId).pipe();
    }

    getNextStatus(status: LegStatus): Observable<LegStatus> {
        let isAuthority = this.permission.isGranted('Pages.Authority.Applications');
        return this.legService.getNextStatus(this.caseId, status).pipe(map(status => {
            // Authority goes to 'finish review' and not to 'release'
            if (isAuthority && status === LegStatus.Release) {
                return LegStatus.FinishReview;
            }
            return status;
        }));
    }

    getPerson(): Observable<LegPersonDto> {
        return of(this.leg.legPerson).pipe(delay(1));
    }

    // getDocuments(status: LegStatus): Observable<LegDocumentGroup[]> {
    //     return this.legDocumentService.getDocumentsForLeg(this.caseId, status);
    // }

    // getFile(fileId: number, step: LegStatus): Observable<GeneralFileDto> {
    //     return this.legDocumentService.getFile(this.caseId, step, fileId);
    // }

    // uploadFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
    //     return this.legDocumentService.uploadFiles(input);
    // }

    // deleteFile(id: number, step: LegStatus): Observable<void> {
    //     return this.legDocumentService.deleteFile(this.caseId, step, id);
    // }
    getFiles(step: LegStatus): Observable<GeneralDocumentGroupDto[]> {
        const input = new GetLegDocumentsInput({ caseId: this.caseId, step: step, requestOwnership: null, isIdentified: null, isAuthorizedSignatory: null });
        return this.legDocumentService.getDocuments(input).pipe(map((output: LegDocumentsOutput) => output.documents));
    }

    getFilesForAuthorizationStep(step: LegStatus, requestOwnership: boolean, isAuthorizedSignatory: boolean = false, acrossAllApplications: boolean = false): Observable<GeneralDocumentGroupDto[]> {
        const input = new GetLegDocumentsInput({ caseId: this.caseId, step: step, requestOwnership: requestOwnership, isIdentified: null, isAuthorizedSignatory: isAuthorizedSignatory });
        return this.legDocumentService.getDocuments(input).pipe(map((output: LegDocumentsOutput) => output.documents));
    }

    getFilesForIdentificationStep(step: LegStatus, requestOwnership: boolean, isIdentified: boolean = false, acrossAllApplications: boolean = false): Observable<GeneralDocumentGroupDto[]> {
        const input = new GetLegDocumentsInput({ caseId: this.caseId, step: step, requestOwnership: requestOwnership, isIdentified: isIdentified, isAuthorizedSignatory: null });
        return this.legDocumentService.getDocuments(input).pipe(map((output: LegDocumentsOutput) => output.documents));
    }

    getGrantedUserIdentificationFile(docId: number): Observable<GeneralFileDto> {
        return this.legDocumentService.getGrantedUserIdentificationFile(docId);
    }

    getFile(step: LegStatus, id: number): Observable<GeneralFileDto> {
        const input = new GetLegFileInput();
        input.caseId = this.caseId;
        input.step = step;
        input.id = id;
        return this.legDocumentService.getFile(input);
    }

    uploadFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.legDocumentService.uploadFiles(input);
    }

    deleteFile(step: LegStatus, id: number): Observable<void> {
        return this.legDocumentService.deleteFile(this.caseId, step, id);
    }

    checkDocuments(): Observable<LegStatus[]> {
        return this.legService.validateThatAllMandatoryDocumentsHaveBeenUploaded(this.caseId);
    }

    releaseLeg(comment: string): Observable<void> {
        return this.legService.releaseLeg(this.caseId, comment);
    }

    getExternalSystemByProfessionId(professionId: string): Observable<ExternalSystemEnum> {
        return this.legService.getExternalRegisterByProfessionId(professionId);
    }

    acceptRegisterCheck(): Observable<LegStatus> {
        this.applicationService.setStepToReview(LegStatus.Person);
        return this.legService.acceptRegisterCheck(this.caseId).pipe(tap((status: LegStatus) => {
            this.leg.currentStep = status;
        }));
    }

    getStepUrl(nextStep: LegStatus): string {
        return this.applicationService.getNextStep(LegService.getUrlForStep(nextStep));
    }

    static getUrlForStep(step: LegStatus): string {
        switch (step) {
            case LegStatus.FinishReview:
                return 'finish-review';
            case LegStatus.Person:
                return 'person';
            case LegStatus.Sponsorship:
                return 'sponsorship';
            case LegStatus.Authorization:
                return 'authorization';
            case LegStatus.Identification:
                return 'identification';
            case LegStatus.Release:
                return 'release';
            default:
                console.error('Invalid status for getUrlForStep');
                return null;
        }
    }

    get status(): ApplicationStatus {
        return this.leg?.application?.status;
    }

    get caseId(): string {
        return this.leg?.application?.caseId;
    }

    getApplications (filter: string, sorting: string, maxResultCount: number, skipCount: number){
        return this.legService.getLegApplications(filter, sorting, maxResultCount, skipCount);
    }

    deleteApplication(): Observable<void> {
        return this.applicationService.deleteApplication();
    }

    getEvaluationComments(): Observable<string> {
        return this.legService.getEvaluationComments(this.applicationService.caseId);
    }

    getPersonId(): Observable<number> {
        return this.legService.getPersonId(this.applicationService.caseId).pipe(delay(1));
    }

    getSponsorshipId(): Observable<number> {
        return this.legService.getSponsorshipId(this.applicationService.caseId).pipe(delay(1));
    }

    // getSponsorship(): Observable<LegSponsorshipDto> {
    //     // return of(this.leg.legSponsorship).pipe(delay(1));
    //     return of(null);
    // }

    // updateLegSponsorship(input: UpdateLegSponsorshipInput): Observable<LegStatus> {
    //     //return this.legService.updateSponsorship(input).pipe();
    //     return of(null);
    // }

    getMyPersonLegs(): Observable<PersonApplicationSummaryDto[]> {
        return this.legService.getMyPersonLegs().pipe();
    }

    getMySponsorshipLegs(): Observable<SponsorshipApplicationSummaryDto[]> {
        return this.legService.getMySponsorshipLegs().pipe();
    }

    // UidSerach Step
    setLegUidSearchStepToReview() {
        this.applicationService.setStepToReview(LegStatus.Sponsorship);
    }

    getUidData(): Observable<LegUidDataDto> {
        return this.uidSearchService.get(this.caseId);
    }

    createOrUpdateUidData(uidData: LegUidDataDto): Observable<any> {
        this.applicationService.setStepToReview(LegStatus.Sponsorship);
        return this.uidSearchService.createOrUpdate(this.caseId, uidData).pipe(
            tap((newStatusId: LegStatus) => {
                this.leg.legUidData = uidData;
                this.leg.currentStep = newStatusId;
            })
        );
    }

    // Authorization Step
    setLegAuthorizationStepToReview() {
        this.applicationService.setStepToReview(LegStatus.Authorization);
    }

    getAuthorization(): Observable<LegAuthorizationDto> {
        return this.authorityService.get(this.caseId);
    }

    createOrUpdateAuthorization(authorization: LegAuthorizationDto): Observable<any> {
        this.applicationService.setStepToReview(LegStatus.Authorization);
        return this.authorityService.createOrUpdate(this.caseId, authorization).pipe(
            tap((newStatusId: LegStatus) => {
                this.leg.authorization = authorization;
                this.leg.currentStep = newStatusId;
            })
        );
    }
    hasConfirmedSoleOwnership(): boolean {
        return this.leg.authorization.isAuthorizedSignatory;
    }



    // Identification Step
    getIdentification(): Observable<GetLegIdentificationOutput> {
        return this.identificationService.get(this.caseId);
    }

    createOrUpdateIdentification(authorization: LegIdentificationDto, isIdentified: boolean): Observable<any> {
        if (isIdentified === false) {
            this.applicationService.setStepToReview(LegStatus.Identification);
        }
        return this.identificationService.createOrUpdate(this.caseId, authorization).pipe(
            tap((newStatusId: LegStatus) => {
                this.leg.currentStep = newStatusId;
            })
        );
    }

    getUserIdentificationDocuments(): Observable<UserIdentificationDto> {
        return this.identificationService.getUserIdentificationDocuments(this.leg.application.userId);
    }
}
